/**
 * Detailansicht einer Sonne bei Klick auf der Karte.
 * Desktop: schwebt neben Klickposition. Mobile: Slide-In vom rechten Rand.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { ClusterContent } from '../network/ContentLoader';
import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import type { SolarEnergyConfig } from '../config/SolarEnergyConfig';
import { axialKey, axialToWorld } from '../math/HexMath';
import { worldToScreen } from '../math/ProjectionUtils';
import { spectralIcon, spectralName, starName, lifetimeRow, energyRow } from './SunDetailContent';
import { isMobile } from './UITheme';

const PANEL_W = 'clamp(200px,35vw,260px)';
const OFFSET = 16;
const SLIDE_MS = 250;
const HOLO_BG = 'linear-gradient(180deg,rgba(8,20,45,0.93),rgba(4,12,28,0.9))';
const HOLO_BRD = '1px solid rgba(100,180,255,0.25)';
const HOLO_GLOW = '-4px 0 20px rgba(80,160,255,0.12),inset 0 1px 0 rgba(100,180,255,0.12)';
const SCAN = 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(100,180,255,0.02) 3px,rgba(100,180,255,0.02) 4px)';
const CLOSE = '<div style="display:flex;justify-content:flex-end;margin-bottom:2px">' +
  '<span data-close style="cursor:pointer;color:rgba(100,180,255,0.4);font-size:18px;' +
  'line-height:1;padding:4px 2px">\u2715</span></div>';

interface StarData {
  entityId: string; spectralClass: string; seed: number; q: number; r: number;
}

interface HexClickEvent { q: number; r: number; screenX: number; screenY: number }

export class SunDetailPanel implements GameModule {
  readonly name = 'SunDetailPanel';

  private _stars = new Map<number, StarData>();
  private _planetCounts: Map<string, number>;
  private _luminosities: Map<string, number>;
  private _energyConfig: SolarEnergyConfig;
  private _lifecycle: ReadonlyMap<string, SunLifecycleEntry> | null = null;
  private _el!: HTMLDivElement;
  private _visible = false;
  private _devMode: boolean;
  private _camera: Camera;
  private _canvas!: HTMLCanvasElement;
  private _hexRadius: number;
  private _worldX = 0;
  private _worldZ = 0;
  private _mobile = false;
  private _anim: Animation | null = null;

  constructor(
    planetCounts: Map<string, number>,
    luminosities: Map<string, number>,
    energyConfig: SolarEnergyConfig,
    camera: Camera, hexRadius: number, devMode = false,
  ) {
    this._planetCounts = planetCounts;
    this._luminosities = luminosities;
    this._energyConfig = energyConfig;
    this._camera = camera;
    this._hexRadius = hexRadius;
    this._devMode = devMode;
  }

  init(engine: Engine): void {
    this._canvas = engine.canvas;
    this._mobile = isMobile();
    this._el = document.createElement('div');

    Object.assign(this._el.style, {
      position: 'fixed', fontFamily: 'monospace',
      fontSize: 'clamp(10px,2.4vw,12px)', color: '#8cf',
      zIndex: '9999', display: 'none',
    });

    if (this._mobile) {
      Object.assign(this._el.style, {
        top: '0', right: '0', bottom: '0', left: 'auto',
        width: 'clamp(220px,55vw,300px)', borderRadius: '10px 0 0 10px',
        background: SCAN + ',' + HOLO_BG, border: 'none', borderLeft: HOLO_BRD,
        boxShadow: HOLO_GLOW,
        overflowY: 'auto', pointerEvents: 'auto',
        padding: '10px 14px',
      });
    } else {
      Object.assign(this._el.style, {
        background: 'rgba(5,8,18,0.92)', border: '1px solid rgba(136,204,255,0.2)',
        borderRadius: '10px', padding: 'clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)',
        width: PANEL_W, pointerEvents: 'none',
      });
    }
    document.body.appendChild(this._el);

    engine.events.on<ClusterContent[]>('sun:stars-available', (c) => this._updateStars(c));
    engine.events.on<ReadonlyMap<string, SunLifecycleEntry>>('sun:lifecycle-update', (d) => {
      this._lifecycle = d;
    });
    engine.events.on<HexClickEvent>('hex:clicked', (d) => {
      const star = this._stars.get(axialKey(d.q, d.r));
      if (star) this._show(star);
      else this._hide();
    });
  }

  update(): void {
    if (!this._visible || this._mobile) return;
    const sp = worldToScreen(this._camera, this._canvas, this._worldX, 0, this._worldZ);
    if (!sp || sp.x < 0 || sp.x > window.innerWidth || sp.y < 0 || sp.y > window.innerHeight) {
      this._hide(); return;
    }
    this._positionAt(sp.x, sp.y);
  }

  dispose(): void { this._el.remove(); }

  private _updateStars(contents: ClusterContent[]): void {
    this._stars.clear();
    for (let c = 0; c < contents.length; c++) {
      for (const s of contents[c].stars) {
        if (s.axial_q == null || s.axial_r == null || !s.entity_id) continue;
        this._stars.set(axialKey(s.axial_q, s.axial_r), {
          entityId: s.entity_id, spectralClass: s.spectral_class ?? 'G',
          seed: s.seed ?? 0, q: s.axial_q, r: s.axial_r,
        });
      }
    }
  }

  private _show(s: StarData): void {
    const [wx, wz] = axialToWorld(s.q, s.r, this._hexRadius);
    this._worldX = wx; this._worldZ = wz;

    const icon = spectralIcon(s.spectralClass);
    const typeName = spectralName(s.spectralClass);
    const name = starName(s.seed);
    const planets = this._planetCounts.get(s.entityId) ?? 0;
    const lc = this._lifecycle?.get(s.entityId);
    const lum = this._luminosities.get(s.entityId) ?? 1;

    const html =
      `<div style="font-size:15px;letter-spacing:1px;margin-bottom:4px">${icon} ${name}</div>` +
      `<div style="color:#aaa;font-size:11px;margin-bottom:8px">${typeName}</div>` +
      `<div style="color:#556;font-size:11px;margin-bottom:10px">` +
      `Q ${s.q} \u00b7 R ${s.r} \u00b7 ${planets} Planet${planets !== 1 ? 'en' : ''}</div>` +
      lifetimeRow(lc, this._devMode) +
      energyRow(lc, lum, this._energyConfig, this._devMode);

    this._el.innerHTML = (this._mobile ? CLOSE : '') + html;

    if (this._mobile) {
      this._el.querySelector('[data-close]')?.addEventListener('click', () => this._hide());
    }

    this._el.style.display = 'block';
    this._visible = true;

    if (this._mobile) {
      this._anim?.cancel();
      this._anim = this._el.animate(
        [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }],
        { duration: SLIDE_MS, easing: 'cubic-bezier(0.22,0.85,0.32,1)', fill: 'forwards' },
      );
    } else {
      this._el.style.opacity = '1';
      const sp = worldToScreen(this._camera, this._canvas, wx, 0, wz);
      if (sp) requestAnimationFrame(() => this._positionAt(sp.x, sp.y));
    }
  }

  private _positionAt(sx: number, sy: number): void {
    if (this._mobile) return;
    this._el.style.transform = '';
    const h = this._el.offsetHeight;
    this._el.style.left = (sx + OFFSET) + 'px';
    this._el.style.top = (sy - h / 2) + 'px';
  }

  private _hide(): void {
    if (!this._visible) return;
    this._visible = false;
    if (this._mobile) {
      this._anim?.cancel();
      this._anim = this._el.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }],
        { duration: SLIDE_MS * 0.7, easing: 'ease-in', fill: 'forwards' },
      );
      this._anim.onfinish = () => { this._el.style.display = 'none'; this._anim = null; };
    } else {
      this._el.style.opacity = '0';
      setTimeout(() => { if (!this._visible) this._el.style.display = 'none'; }, 200);
    }
  }
}
